import { useState } from 'react';
import { contentItemService } from '@/services/content-item.service';
import {
  ContentItem,
  ContentItemFormData,
  ContentMediaType,
  ContentStatus,
} from '@/types/contentItem';

interface ContentItemFilters {
  status?: ContentStatus;
  mediaType?: ContentMediaType;
  categoryId?: string;
  search?: string;
}

export const useContentItems = () => {
  const [items, setItems] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [filters, setFilters] = useState<ContentItemFilters>({});
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const limit = 20;

  // Accepts explicit overrides because React state updates are async and
  // callers frequently change page and filters in the same tick.
  const listContentItems = async (overrides?: {
    offset?: number;
    filters?: ContentItemFilters;
  }) => {
    const nextOffset = overrides?.offset ?? offset;
    const nextFilters = overrides?.filters ?? filters;
    setLoading(true);
    try {
      const result = await contentItemService.listContentItems({
        limit,
        offset: nextOffset,
        ...nextFilters,
      });
      setItems(result.data);
      setTotal(result.total);
      setError(false);
    } catch (err) {
      setError(true);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const createContentItem = async (data: ContentItemFormData): Promise<ContentItem> => {
    const result = await contentItemService.createContentItem(data);
    await listContentItems();
    return result;
  };

  const updateContentItem = async (
    id: string,
    data: ContentItemFormData,
  ): Promise<ContentItem> => {
    const result = await contentItemService.updateContentItem(id, data);
    await listContentItems();
    return result;
  };

  const deleteContentItem = async (id: string): Promise<void> => {
    await contentItemService.deleteContentItem(id);
    await listContentItems();
  };

  const updateStatus = async (id: string, status: ContentStatus): Promise<void> => {
    setTogglingId(id);
    try {
      await contentItemService.updateContentItemStatus(id, status);
      await listContentItems();
    } finally {
      setTogglingId(null);
    }
  };

  return {
    items,
    loading,
    error,
    total,
    offset,
    setOffset,
    limit,
    filters,
    setFilters,
    togglingId,
    listContentItems,
    createContentItem,
    updateContentItem,
    deleteContentItem,
    updateStatus,
  };
};
